import { InputRef, Modal } from 'antd';
import { AppModal } from '@components/form/Modal';
import { Checkbox } from 'antd';
import { InviteMemberModalProps } from '../subscription.types';
import { Controller } from 'react-hook-form';
import { useRef } from 'react';
import { AppInput } from '@components/form/input/app-input';
import { AppSelect } from '@components/form/select/app-select';
import { AppPrimaryButton } from '@components/form/button/app-primary-button';
import { FormFieldError } from '@components/form/text-area/app-text-area';
import { MEMBER_INVITE_ROLE_CHOICE } from '@shared/constants';
import { DowngradePlanModal } from './downgrade-plan-cancel-modal';
import CancelledSubscriptionInfoModal from './cancelled-subscription-info-modal';
import { PaymentMethodsList } from './payment-methods-list';
import { FormWrapper } from '@components/form/form-wrapper/form-wrapper';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';
import { AppAlert } from '@components/antd';
import { InrPaymentAlert } from '@components/inr-payment-alert';

export const InviteMemberModal = (props: InviteMemberModalProps) => {
  const { t } = useTranslation();
  const {
    form,
    inviteModal,
    paymentModal,
    estimateAmount,
    checkScheduledDowngrade,
    downgradeModal,
    subsCancelledModal,
    scheduledSubscription,
    errorAlert,
    handlePaymentProcess,
  } = props.inviteController;

  const nameInputRef = useRef<InputRef>(null);

  const handleCloseModal = () => {
    inviteModal.handleClose();
    form.reset();
    props.inviteController.paymentMethodsList.setDefaultPaymentMethod();
  };

  return (
    <>
      <DowngradePlanModal
        open={downgradeModal.open}
        hideModal={downgradeModal.handleClose}
        onDowngradeCancelled={() => {
          downgradeModal.handleClose();
          handlePaymentProcess();
        }}
        isCancelled={scheduledSubscription?.isCancelled}
      />
      <CancelledSubscriptionInfoModal
        open={subsCancelledModal.open}
        onClose={subsCancelledModal.handleClose}
        hideModal={() => {
          subsCancelledModal.handleClose();
          inviteModal.handleClose();
          form.reset();
          paymentModal.handleClose();
        }}
        purpose={t('inviting_members')}
      />
      <Modal
        classNames={{ content: '!max-w-[420px]' }}
        centered
        onCancel={handleCloseModal}
        open={inviteModal.open}
        afterOpenChange={(isOpen) => isOpen && nameInputRef.current?.focus()}
        title={
          <AppModal.Header>
            <AppModal.Title className="capitalize!">{t('invite_member', { count: 1 })}</AppModal.Title>
            <AppModal.SubTitle>
              {t('enter_required_details_to_invite_member_charged_proportionately_for_each')}
            </AppModal.SubTitle>
          </AppModal.Header>
        }
        className="prevent-click-outside"
        footer={
          <AppModal.Footer>
            <AppModal.Button className="w-full capitalize!" onClick={handleCloseModal}>
              {t('cancel')}
            </AppModal.Button>
            <AppModal.Button
              className="w-full capitalize!"
              color="primary"
              loading={props.inviteController.estimateState.loading}
              onClick={form.handleSubmit(props.inviteController.onSubmit)}
            >
              {t('send_invite')}
            </AppModal.Button>
          </AppModal.Footer>
        }
      >
        <AppModal.Body>
          <FormWrapper onSubmit={form.handleSubmit(props.inviteController.onSubmit)} className="w-full space-y-5 mb-6">
            <div className="flex flex-col gap-4">
              <AppInput
                ref={nameInputRef}
                name="name"
                control={form.control}
                label={capitalize(t('full_name'))}
                placeholder={t('enter_member_name')}
                allowClear
                required
              />
              <AppInput
                name="email"
                control={form.control}
                label={capitalize(t('email'))}
                placeholder={t('enter_member_email')}
                required
                type="email"
                allowClear
              />
              <AppSelect
                name="role"
                control={form.control}
                label={capitalize(t('role', { count: 1 }))}
                placeholder={t('select_to_assign_role')}
                className="app-select"
                classNames={{ popup: { root: 'prevent-click-outside' } }}
                options={MEMBER_INVITE_ROLE_CHOICE}
                allowClear
                required
                data-test-id="role_select"
              />
              <Controller
                name="terms"
                control={form.control}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <div>
                    <div className="flex gap-2 items-start">
                      <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} id="terms" />
                      <label className="text-sm font-normal leading-tight" htmlFor="terms">
                        {t('i_understand_inviting_new_member_results_in_prorated_charge_now')}
                      </label>
                    </div>
                    <FormFieldError message={error?.message} />
                  </div>
                )}
              />
            </div>
          </FormWrapper>
        </AppModal.Body>
      </Modal>

      <AppModal
        title={
          <AppModal.Header>
            <AppModal.Title>{capitalize(t('choose_your_payment_method'))}</AppModal.Title>
            <AppModal.SubTitle>{t('complete_payment_to_send_the_member_invite')}</AppModal.SubTitle>
          </AppModal.Header>
        }
        open={paymentModal.open}
        onCancel={() => {
          paymentModal.handleClose();
          props.inviteController.paymentMethodsList.setDefaultPaymentMethod();
        }}
        className="prevent-click-outside"
        classNames={{ content: '!max-w-[420px]' }}
        footer={
          <div className="space-y-4">
            <AppPrimaryButton
              className="w-full"
              onClick={checkScheduledDowngrade}
              loading={props?.inviteController?.paymentMethodsList?.processing}
              disabled={
                !estimateAmount ||
                !props?.inviteController?.paymentMethodsList?.paymentMethod ||
                props?.inviteController?.paymentMethodsList?.paymentMethod?.status !== 'valid'
              }
            >
              <p className="capitalize!">{t('pay_now')}</p>
            </AppPrimaryButton>
            <PaymentMethodsList.CompliantLabel />
          </div>
        }
      >
        <AppModal.Body>
          <div className="space-y-4 mt-4">
            {errorAlert && <AppAlert showIcon type="error" message={errorAlert} />}

            <div>
              <p className="text-xs font-medium text-text-500 uppercase mb-1.5">{t('amount')}</p>
              <div className="p-3 border border-gray-500 rounded-lg text-center leading-none">
                $ {estimateAmount / 100}
              </div>
            </div>
            <PaymentMethodsList
              loading={props.inviteController?.paymentMethodsList?.listSubscriptionResult?.loading}
              paymentCards={props.inviteController?.paymentMethodsList?.paymentCards}
              paymentPaypal={props.inviteController?.paymentMethodsList?.paymentPaypal}
              selected={props.inviteController?.paymentMethodsList?.paymentMethod}
              onChange={(method) => props.inviteController?.paymentMethodsList?.setPaymentMethod(method)}
            />
            <InrPaymentAlert flow="member-invite" />
          </div>
        </AppModal.Body>
      </AppModal>
    </>
  );
};
